/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useState, useEffect } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import { CustomItemComponentProps } from '@atlaskit/menu';
import { B400 } from '@atlaskit/theme/colors';
import { setGlobalTheme, token } from '@atlaskit/tokens';
import { StaticQuery, graphql } from 'gatsby';
import { LOCAL_STORAGE_THEME_KEY } from '../../utils/constants';

const themeData = {
  light: {
    label: 'Light theme',
    description: 'Design tokens (beta)',
    imageSrc: 'modeLightThumb',
  },
  dark: {
    label: 'Dark theme',
    description: 'Design tokens (beta)',
    imageSrc: 'modeDarkThumb',
  },
  auto: {
    label: 'Same as system',
    description: 'Match operating system appearance',
    imageSrc: 'modeLightThumb',
  },
  none: {
    label: 'Original light theme',
    description: 'No design tokens',
    imageSrc: 'modeNoneThumb',
  },
};

const Item = ({
  label,
  description,
  isSelected,
  onClick,
  src,
}: {
  label: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  src?: string;
  srcDark?: string;
}) => (
  <DropdownItem
    isSelected={isSelected}
    onClick={onClick}
    description={description}
    component={({ children, ...triggerProps }: CustomItemComponentProps) => {
      const buttonStyles = isSelected
        ? {
            boxShadow: `inset 2px 0px 0px ${token(
              'color.border.selected',
              B400,
            )}`,
          }
        : {};
      return (
        <button
          {...triggerProps}
          role={isVoiceOverSupported() ? 'radio' : 'menuitemradio'}
          aria-checked={isSelected}
          style={buttonStyles}
        >
          <img
            alt=""
            src={src}
            css={{
              width: '4rem',
              height: '3rem',
              borderRadius: token('border.radius.100', '3px'),
            }}
          />
          <div css={{ marginLeft: token('space.150', '12px') }}>{children}</div>
        </button>
      );
    }}
  >
    {label}
  </DropdownItem>
);

type themeOptions = 'light' | 'dark' | 'auto' | 'none';

export default function Settings() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<themeOptions>('none');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');

  const baseTheme: Parameters<typeof setGlobalTheme>[0] = {
    spacing: 'spacing',
    typography: 'typography',
    shape: 'shape',
  };

  useEffect(() => {
    const localStorageTheme = localStorage.getItem(LOCAL_STORAGE_THEME_KEY);

    if (
      localStorageTheme === 'light' ||
      localStorageTheme === 'dark' ||
      localStorageTheme === 'auto'
    ) {
      // If a light/dark/auto theme has previously been set, use that theme
      setCurrentTheme(localStorageTheme);
      setGlobalTheme({ ...baseTheme, colorMode: localStorageTheme });
    } else if (localStorageTheme === 'none') {
      // If 'none' has previously been set, use that (do not load tokens)
      setCurrentTheme('none');
    } else {
      // If nothing set previously, set default theme to 'auto' and load tokens
      setCurrentTheme('auto');
      setGlobalTheme({ ...baseTheme, colorMode: 'auto' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (e: MediaQueryListEvent) =>
      setSystemTheme(e.matches ? 'dark' : 'light');

    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  const setTheme = (theme: themeOptions) => {
    setCurrentTheme(theme);
    localStorage.setItem(LOCAL_STORAGE_THEME_KEY, theme);
    if (theme === 'none') {
      const element = document.documentElement;
      element.removeAttribute('data-theme');
      element.removeAttribute('data-color-mode');
    } else {
      setGlobalTheme({ ...baseTheme, colorMode: theme });
    }
    return;
  };

  return (
    <StaticQuery
      query={graphql`
        query modeLightThumb {
          modeLightThumb: file(
            relativePath: { eq: "theme-thumbs/LightThumb.svg" }
          ) {
            publicURL
          }
          modeDarkThumb: file(
            relativePath: { eq: "theme-thumbs/DarkThumb.svg" }
          ) {
            publicURL
          }
          modeNoneThumb: file(
            relativePath: { eq: "theme-thumbs/NoneThumb.svg" }
          ) {
            publicURL
          }
        }
      `}
      render={(data) => (
        <DropdownMenu
          placement="bottom-end"
          isOpen={isOpen}
          onOpenChange={() => setIsOpen(!isOpen)}
          trigger={({ triggerRef, onClick, ...triggerProps }) => (
            <Button
              ref={triggerRef}
              onClick={(e) => {
                // @ts-expect-error
                onClick(e);
                setIsOpen(!isOpen);
              }}
              {...triggerProps}
            >
              Theme
            </Button>
          )}
        >
          <DropdownItemCheckboxGroup id="color-theme">
            {(['light', 'dark', 'auto', 'none'] as const).map(
              (theme: themeOptions, i) => (
                <Item
                  key={i}
                  label={themeData[theme].label}
                  description={themeData[theme].description}
                  isSelected={currentTheme === theme}
                  onClick={() => setTheme(theme)}
                  src={
                    data[
                      themeData[theme === 'auto' ? systemTheme : theme].imageSrc
                    ].publicURL
                  }
                />
              ),
            )}
          </DropdownItemCheckboxGroup>
        </DropdownMenu>
      )}
    />
  );
}
/*
 * --- Copied from DropdownItemRadio
 * Making sure we can fallback to browsers doesn't support voice over -
 * we would using menuitemcheckbox / menuitemradio for these that supports voice over, and
 * will fallback to checkbox / radio for these doesn't
 */
const isVoiceOverSupported = () =>
  /Mac OS X/.test(canUseDOM() ? navigator.userAgent : '');

const canUseDOM = () =>
  Boolean(
    typeof window !== 'undefined' &&
      window.document &&
      window.document.createElement,
  );
